import { ITelemetryItem } from "@microsoft/applicationinsights-web";

const APP_ROLE_SUFFIX = "-desktop";

export const UNBRANDED_APP_ROLE = "kno2-desktop-unbranded";

/**
 * Derive the cloud role from the deploy-time brand.
 *
 * @remarks
 * `brand.name` comes from the Octopus variable `Env:brandName`, substituted into
 * environment.prod.ts at deploy time. If that substitution has not run, the raw
 * `#{Env:brandName}` token arrives here - hence the placeholder check.
 *
 * The result is slugified because alert queries filter on the role, so it must stay
 * `[a-z0-9-]` whatever casing or spacing the variable happens to hold.
 *
 * @param brandName - The deploy-time brand name (`environment.brand.name`).
 * @returns The slugified cloud role, or {@link UNBRANDED_APP_ROLE} when the brand cannot be determined.
 *
 * @example
 * ```ts
 * resolveAppRoleName("WellSky");          // "wellsky-desktop"
 * resolveAppRoleName("Kno2");             // "kno2-desktop"
 * resolveAppRoleName("#{Env:brandName}"); // "kno2-desktop-unbranded"
 * resolveAppRoleName(undefined);          // "kno2-desktop-unbranded"
 * ```
 */
export function resolveAppRoleName(brandName: unknown): string {
    if (typeof brandName !== "string" || brandName.includes("#{")) return UNBRANDED_APP_ROLE;
    const slug = brandName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
    if (!slug) return UNBRANDED_APP_ROLE;
    return `${slug}${APP_ROLE_SUFFIX}`;
}

/**
 * Stamp the cloud role onto a telemetry envelope.
 *
 * @remarks
 * `tags` is a string-keyed map - the SDK's own ITelemetryItem deprecated the array form in 4.1.0 -
 * so it is defaulted to an object. A string key set on an array would not survive serialization,
 * and the role would silently vanish.
 *
 * @param envelope - The telemetry item passed through the initializer chain.
 * @param appRoleName - The cloud role from {@link resolveAppRoleName}.
 *
 * @example
 * ```ts
 * const envelope = { tags: undefined };
 * applyAppRoleName(envelope, "wellsky-desktop");
 * envelope.tags["ai.cloud.role"]; // "wellsky-desktop"
 * ```
 */
export function applyAppRoleName(envelope: ITelemetryItem, appRoleName: string): void {
    envelope.tags = envelope.tags || {};
    envelope.tags["ai.cloud.role"] = appRoleName;
}
